// /account: nickname, email, play-time emails, my songs, the guided tours, data export and account
// deletion (with the CC BY explanation). New accounts land here to choose a nickname.
import { useEffect, useState, type FormEvent } from "react";
import { checkNickname } from "@eb/shared";
import { api, ApiError } from "../api";
import { Link, navigate, useSearch, useTitle } from "../lib/router";
import { deleteAccount, saveAccountSettings, setNickname, signOut, useSession } from "../session";
import { Avatar } from "../ui/Avatar";
import { TourSettings } from "../tour/TourLaunch";
import { MySongs } from "./MySongs";

export function Account() {
  useTitle("Account");
  const { user, loaded } = useSession();
  const params = useSearch();
  const welcome = params.get("welcome") === "1";
  const next = params.get("next");
  const [nick, setNick] = useState(user?.nickname ?? "");
  const [nickMsg, setNickMsg] = useState("");
  const [confirm, setConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleted, setDeleted] = useState<{ keptLoops: number; removedLoops: number } | null>(null);
  useEffect(() => setNick(user?.nickname ?? ""), [user?.nickname]);

  if (deleted)
    return (
      <main id="main" className="page narrow">
        <h1>Your account is deleted</h1>
        <p data-testid="deleted">
          Your email and account are gone. {deleted.keptLoops} loop(s) that already played stay in the song as “former
          contributor” (CC BY 4.0). {deleted.removedLoops} loop(s) that hadn't played yet were removed.
        </p>
        <Link className="btn" href="/">
          Back to the song
        </Link>
      </main>
    );
  if (!loaded)
    return (
      <main id="main" className="page narrow">
        <p className="muted">Loading…</p>
      </main>
    );
  if (!user)
    return (
      <main id="main" className="page narrow">
        <h1>Account</h1>
        <p>You're not signed in.</p>
        <Link className="btn btn-primary" href="/signin?next=/account">
          Sign in
        </Link>
      </main>
    );

  async function saveNick(e: FormEvent) {
    e.preventDefault();
    const local = checkNickname(nick.trim());
    if (!local.ok) return setNickMsg(local.reason ?? "Not allowed.");
    try {
      await setNickname(nick.trim());
      setNickMsg("Saved.");
      if (welcome && next) navigate(next);
    } catch (err) {
      setNickMsg(err instanceof ApiError ? err.message : "Couldn't save.");
    }
  }

  return (
    <main id="main" className="page narrow">
      <h1>{welcome && !user.nickname ? "Welcome! Choose a nickname" : "Your account"}</h1>
      <form className="panel stack" onSubmit={saveNick} aria-labelledby="nick-h">
        <h2 id="nick-h">Nickname</h2>
        <p className="muted">
          Shown on your loops and your profile, and used for the CC BY credit. 3–24 letters, digits, dots, dashes or
          underscores.
        </p>
        <div className="row">
          <label className="sr-only" htmlFor="nick">
            Nickname
          </label>
          <input
            id="nick"
            type="text"
            value={nick}
            onChange={(e) => setNick(e.target.value)}
            maxLength={24}
            autoComplete="nickname"
            data-testid="nickname"
          />
          <button className="btn btn-primary" data-testid="nickname-save">
            Save
          </button>
        </div>
        {nickMsg && (
          <p role="status" data-testid="nickname-msg">
            {nickMsg}
          </p>
        )}
      </form>

      {user.nickname && (
        <section className="panel stack" aria-labelledby="profile-h">
          <h2 id="profile-h">Public profile</h2>
          <div className="row">
            <Avatar nickname={user.nickname} size={48} />
            <p>
              Your avatar is drawn from your nickname. Add a bio and a link on{" "}
              <Link href={`/u/${encodeURIComponent(user.nickname)}`} data-testid="my-profile-link">
                your profile page
              </Link>
              .
            </p>
          </div>
        </section>
      )}

      <section className="panel stack" aria-labelledby="acc-h">
        <h2 id="acc-h">Email and reminders</h2>
        <p>
          Signed in as <strong>{user.email}</strong>
          {user.isAdmin && (
            <>
              {" "}
              · <Link href="/admin">Admin</Link>
            </>
          )}
        </p>
        <label className="toggle">
          <input
            type="checkbox"
            checked={user.reminders}
            onChange={(e) => void saveAccountSettings({ reminders: e.target.checked })}
          />
          <span>
            Email me when my song's play time is fixed (with a calendar entry) and again shortly before it plays
          </span>
        </label>
        {user.nickname && <Link href={`/u/${encodeURIComponent(user.nickname)}`}>Your public profile</Link>}
        <div>
          <button type="button" className="btn" onClick={() => void signOut().then(() => navigate("/"))}>
            Sign out
          </button>
        </div>
      </section>

      {user.nickname && <MySongs />}

      <TourSettings />

      <section className="panel stack" aria-labelledby="data-h">
        <h2 id="data-h">Your data</h2>
        <p>Download everything we store about you (account, drafts, songs, Wizard suggestions, reports) as JSON.</p>
        <div>
          <button type="button" className="btn" onClick={() => void downloadExport()} data-testid="export">
            Download my data
          </button>
        </div>
      </section>

      <section className="panel stack danger" aria-labelledby="del-h">
        <h2 id="del-h">Delete account</h2>
        <p>Deleting removes your account and email address right away.</p>
        <p>
          <strong>Your songs that have already played stay in the endless song</strong>: you licensed them under CC BY
          4.0, so they keep playing in replays credited to “former contributor”. Loops that haven't played yet are
          removed and replaced by Wizard fillers.
        </p>
        <label className="stack-label">
          <span>
            Type <strong>delete my account</strong> to confirm
          </span>
          <input
            type="text"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            data-testid="delete-confirm"
          />
        </label>
        <div>
          <button
            type="button"
            className="btn btn-danger"
            disabled={confirm.trim().toLowerCase() !== "delete my account" || deleting}
            onClick={async () => {
              setDeleting(true);
              try {
                setDeleted(await deleteAccount(confirm));
              } finally {
                setDeleting(false);
              }
            }}
            data-testid="delete-account"
          >
            Delete my account
          </button>
        </div>
      </section>
    </main>
  );
}

/** Download the GDPR export as a file. */
async function downloadExport(): Promise<void> {
  const data = await api<unknown>("GET", "/api/me/export");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
  a.download = "endless-beat-export.json";
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
